import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'pt' | 'en';

interface Translations {
  pt: Record<string, string>;
  en: Record<string, string>;
}

const translations: Translations = {
  pt: {
    // Header
    'header.dashboard': 'Dashboard',
    'header.users': 'Usuários',
    'header.organizations': 'Organizações',
    'header.chat': 'Chat',
    'header.rules': 'Regras/Prompt',
    'header.results': 'Resultados',
    'header.account': 'Conta',
    'header.accountSettings': 'Configurações da Conta',
    'header.preferences': 'Preferências',
    'header.signOut': 'Sair',
    
    // Subtitles
    'subtitle.dashboard': 'Visão geral e análises',
    'subtitle.users': 'Gerenciar membros da equipe',
    'subtitle.organizations': 'Gerenciamento de empresas',
    'subtitle.chat': 'Análise com IA',
    'subtitle.rules': 'Regras e políticas de compliance',
    'subtitle.results': 'Resultados das análises',
    'subtitle.account': 'Configurações do perfil',
    'subtitle.default': 'Plataforma Legal com IA',
    
    // Sidebar
    'sidebar.dashboard': 'Agentes',
    'sidebar.users': 'Usuários',
    'sidebar.organizations': 'Organizações',
    'sidebar.chat': 'Chat',
    'sidebar.rules': 'Regras/Prompt',
    'sidebar.results': 'Resultados',
    'sidebar.folders': 'Pastas',
    'home.titulo': 'Por onde começar?',
    'home.subtitulo': 'Escolha uma área para continuar.',
    'home.descricao.dashboard': 'Visão geral e agentes de IA',
    'home.descricao.users': 'Gerenciar quem acessa a plataforma',
    'home.descricao.organizations': 'Empresas e seus dados',
    'home.descricao.chat': 'Conversar com a LIA sobre um caso',
    'home.descricao.rules': 'Prompts e regras de análise',
    'home.descricao.folders': 'Documentos organizados por pasta',
    'home.descricao.results': 'Análises já concluídas',
    
    // Preferences
    'preferences.language': 'Idioma',
    'preferences.portuguese': 'Português',
    'preferences.english': 'English',
    'preferences.selectLanguage': 'Selecione o idioma',
    
    // Users Page
    'users.title': 'Usuários',
    'users.subtitle': 'Gerenciar membros da equipe',
    'users.addNew': 'Novo Usuário',
    'users.search': 'Buscar usuários...',
    'users.name': 'Nome',
    'users.email': 'Email',
    'users.role': 'Função',
    'users.status': 'Status',
    'users.actions': 'Ações',
    'users.active': 'Ativo',
    'users.inactive': 'Inativo',
    'users.admin': 'Administrador',
    'users.user': 'Usuário',
    'users.manager': 'Gerente',
    'users.confirmDelete': 'Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita!',
    'users.successMessage': 'Usuário foi removido com sucesso!',
    'users.successUpdated': 'Usuário foi atualizado com sucesso!',
    
    // Organizations Page
    'organizations.title': 'Organizações',
    'organizations.subtitle': 'Gerenciamento de empresas',
    'organizations.addNew': 'Nova Organização',
    'organizations.search': 'Buscar organizações...',
    'organizations.name': 'Nome',
    'organizations.domain': 'Domínio',
    'organizations.users': 'Usuários',
    'organizations.status': 'Status',
    'organizations.actions': 'Ações',
    'organizations.confirmDelete': 'Tem certeza que deseja excluir esta organização? Esta ação não pode ser desfeita!',
    'organizations.successMessage': 'Organização foi removida com sucesso!',
    'organizations.successUpdated': 'Organização foi atualizada com sucesso!',
    'organizations.successAdded': 'Organização foi criada com sucesso!',
    
    // Compliance Page
    'compliance.title': 'Análise de Compliance',
    'compliance.subtitle': 'Análise automatizada com IA',
    'compliance.copyPaste': 'Copy & Paste',
    'compliance.copyPasteDesc': 'Cole seu texto diretamente',
    'compliance.uploadFile': 'Upload de Arquivo',
    'compliance.uploadFileDesc': 'Faça upload de documentos',
    'compliance.form': 'Formulário',
    'compliance.formDesc': 'Preencha um formulário estruturado',
    'compliance.startAnalysis': 'Iniciar Análise',
    'compliance.selectFile': 'Selecione ou arraste um arquivo',
    'compliance.dragFile': 'Clique ou arraste um arquivo aqui',
    'compliance.acceptedFormats': 'Formatos aceitos: PDF, DOCX, TXT',
    'compliance.send': 'Enviar',
    'compliance.fillForm': 'Preencher formulário',
    'compliance.name': 'Nome',
    'compliance.document': 'Documento',
    'compliance.description': 'Descrição',
    'compliance.namePlaceholder': 'Seu nome',
    'compliance.documentPlaceholder': 'CPF ou CNPJ',
    'compliance.descriptionPlaceholder': 'Descreva o propósito deste documento...',
    
    // Results Page
    'results.title': 'Resultados',
    'results.subtitle': 'Resultados das análises de compliance',
    'results.search': 'Buscar resultados...',
    'results.date': 'Data',
    'results.type': 'Tipo',
    'results.status': 'Status',
    'results.score': 'Pontuação',
    'results.actions': 'Ações',
    'results.pending': 'Pendente',
    'results.completed': 'Concluído',
    'results.failed': 'Falhou',
    'results.operator': 'Operador',
    'results.content': 'Conteúdo',
    'results.noContent': 'Nenhum conteúdo disponível',
    'results.download': 'Baixar',
    'results.copy': 'Copiar',
    'results.seeConversation': 'Ver conversa',
    'results.noResults': 'Nenhum resultado encontrado',
    'results.details': 'Detalhes do Resultado',
    'results.copyContent': 'Copiar Conteúdo',
    'results.copySuccess': 'Conteúdo copiado para a área de transferência!',
    
    // Rules Page
    'rules.title': 'Regras/Prompt',
    'rules.subtitle': 'Regras e políticas de compliance',
    'rules.addNew': 'Nova Regra',
    'rules.search': 'Buscar regras...',
    'rules.name': 'Nome',
    'rules.description': 'Descrição',
    'rules.category': 'Categoria',
    'rules.status': 'Status',
    'rules.actions': 'Ações',
    'rules.confirmDelete': 'Tem certeza que deseja excluir esta regra? Esta ação não pode ser desfeita!',
    'rules.successMessage': 'Regra foi removida com sucesso!',
    'rules.successUpdated': 'Regra foi atualizada com sucesso!',
    
    // Forms - User
    'form.user.newUser': 'Novo Usuário',
    'form.user.editUser': 'Editar Usuário',
    'form.user.userDetails': 'Detalhes do Usuário',
    'form.user.name': 'Nome',
    'form.user.namePlaceholder': 'Nome',
    'form.user.email': 'E-mail',
    'form.user.emailPlaceholder': 'exemplo@exemplo.com',
    'form.user.password': 'Senha',
    'form.user.passwordPlaceholder': '********',
    'form.user.profile': 'Perfil',
    'form.user.profilePlaceholder': 'Selecione um perfil',
    'form.user.organization': 'Organização',
    'form.user.organizationPlaceholder': 'Selecione uma organização',
    'form.user.organizationPredefined': 'Organização (Pré-definida)',
    'form.user.phone': 'Telefone',
    'form.user.phonePlaceholder': '+55 (xx) xxxxx-xxxx ou +1 (xxx) xxx-xxxx',
    'form.user.role': 'Função',
    'form.user.profileAdmin': 'Administrador',
    'form.user.profileUser': 'Usuário',
    'form.user.profileManager': 'Gerente',
    'form.user.backToUsers': 'Voltar para lista de usuários',
    'form.user.add': 'Adicionar',
    'form.user.update': 'Atualizar',
    'form.user.processing': 'Processando...',
    'form.user.addConfirmation': 'Confirmar Adição',
    'form.user.updateConfirmation': 'Confirmar Atualização',
    'form.user.addConfirmText': 'Tem certeza que deseja adicionar este usuário?',
    'form.user.updateConfirmText': 'Tem certeza que deseja atualizar este usuário?',
    'form.user.notSpecified': 'Não especificado',
    'form.user.userNotFound': 'Usuário não encontrado',
    'form.user.loadingOrganization': 'Carregando organização...',
          'form.user.permissions': 'Permissões',
      'form.user.mfaActive': 'MFA Ativo',
      'form.user.mfaDescription': 'Quando ativo, o usuário precisará inserir um código adicional no login',

    // Forms - Organization
    'form.org.newOrganization': 'Nova Organização',
    'form.org.editOrganization': 'Editar Organização',
    'form.org.organizationDetails': 'Detalhes da Organização',
    'form.org.name': 'Nome',
    'form.org.namePlaceholder': 'Nome',
    'form.org.email': 'E-mail',
    'form.org.emailPlaceholder': 'exemplo@exemplo.com',
    'form.org.cnpjTaxId': 'CNPJ / Tax ID',
    'form.org.cnpjPlaceholder': '00.000.000/0001-00',
    'form.org.contactName': 'Nome do Contato',
    'form.org.contactNamePlaceholder': 'Nome do contato',
    'form.org.phone': 'Telefone',
    'form.org.phonePlaceholder': '+55 (xx) xxxxx-xxxx',
    'form.org.backToOrganizations': 'Voltar para lista de organizações',
    'form.org.add': 'Adicionar',
    'form.org.update': 'Atualizar',
    'form.org.addConfirmation': 'Confirmar Adição',
    'form.org.updateConfirmation': 'Confirmar Atualização',
    'form.org.addConfirmText': 'Tem certeza que deseja adicionar esta organização?',
    'form.org.updateConfirmText': 'Tem certeza que deseja atualizar esta organização?',
    'form.org.organizationNotFound': 'Organização não encontrada',

    // Forms - Rules
    'form.rule.newRule': 'Nova Regra',
    'form.rule.editRule': 'Editar Regra',
    'form.rule.addRule': 'Adicionar Regra',
    'form.rule.ruleDetails': 'Detalhes da Regra',
    'form.rule.detailsOfRule': 'Detalhes da Regra',
    'form.rule.ruleName': 'Nome da Regra',
    'form.rule.ruleNamePlaceholder': 'Digite o nome da regra',
    'form.rule.instructions': 'Instruções',
    'form.rule.instructionsPlaceholder': 'Digite suas instruções aqui...',
    'form.rule.instructionsDescription': 'Coloque abaixo as instruções que você quer que a IA siga no tratamento dos seus dados. Quanto mais detalhes forem fornecidos, melhor será a precisão do sistema.',
    'form.rule.name': 'Nome',
    'form.rule.prompt': 'Prompt',
    'form.rule.backToRules': 'Voltar para lista de regras',
    'form.rule.save': 'Salvar',
    'form.rule.saving': 'Salvando...',
    'form.rule.successCreated': 'A regra foi criada com sucesso!',
    'form.rule.successUpdated': 'A regra foi atualizada com sucesso!',
    'form.rule.ruleNotFound': 'Regra não encontrada',
    'form.rule.ruleNotFoundDesc': 'Não foi possível encontrar os detalhes da regra solicitada.',
    'form.rule.ruleNameRequired': 'Por favor, digite um nome para a regra',
    'form.rule.instructionsRequired': 'Por favor, digite as instruções para a regra',
    'form.rule.saveError': 'Ocorreu um erro ao salvar as instruções. Por favor, tente novamente.',

    // Validation Messages
    'validation.emailInvalid': 'Por favor, digite um endereço de email válido',
    'validation.passwordMinLength': 'A senha deve ter pelo menos 8 caracteres',
    'validation.phoneInvalid': 'Por favor, digite um número de telefone válido: +55 (xx) xxxxx-xxxx ou +1 (xxx) xxx-xxxx',
    'validation.cnpjInvalid': 'Por favor, digite um CNPJ válido no formato 00.000.000/0001-00',

    // Common
    'common.save': 'Salvar',
    'common.cancel': 'Cancelar',
    'common.close': 'Fechar',
    'common.loading': 'Carregando...',
    'common.edit': 'Editar',
    'common.delete': 'Excluir',
    'common.view': 'Visualizar',
    'common.new': 'Novo',
    'common.search': 'Buscar',
    'common.filter': 'Filtrar',
    'common.export': 'Exportar',
    'common.import': 'Importar',
    'common.refresh': 'Atualizar',
    'common.success': 'Sucesso',
  },
  en: {
    // Header
    'header.dashboard': 'Dashboard',
    'header.users': 'Users',
    'header.organizations': 'Organizations',
    'header.chat': 'Chat',
    'header.rules': 'Rules',
    'header.results': 'Results',
    'header.account': 'Account',
    'header.accountSettings': 'Account Settings',
    'header.preferences': 'Preferences',
    'header.signOut': 'Sign Out',
    
    // Subtitles
    'subtitle.dashboard': 'Overview & Analytics',
    'subtitle.users': 'Manage team members',
    'subtitle.organizations': 'Company management',
    'subtitle.chat': 'AI-powered analysis',
    'subtitle.rules': 'Compliance rules & policies',
    'subtitle.results': 'Analysis results',
    'subtitle.account': 'Profile settings',
    'subtitle.default': 'AI Legal Platform',
    
    // Sidebar
    'sidebar.dashboard': 'Agents',
    'sidebar.users': 'Users',
    'sidebar.organizations': 'Organizations',
    'sidebar.chat': 'Chat',
    'sidebar.rules': 'Rules',
    'sidebar.results': 'Results',
    'sidebar.folders': 'Folders',
    'home.titulo': 'Where do you want to start?',
    'home.subtitulo': 'Pick an area to continue.',
    'home.descricao.dashboard': 'Overview and AI agents',
    'home.descricao.users': 'Manage who accesses the platform',
    'home.descricao.organizations': 'Companies and their data',
    'home.descricao.chat': 'Talk to LIA about a case',
    'home.descricao.rules': 'Prompts and analysis rules',
    'home.descricao.folders': 'Documents organized in folders',
    'home.descricao.results': 'Completed analyses',
    
    // Preferences
    'preferences.language': 'Language',
    'preferences.portuguese': 'Português',
    'preferences.english': 'English',
    'preferences.selectLanguage': 'Select language',
    
    // Users Page
    'users.title': 'Users',
    'users.subtitle': 'Manage team members',
    'users.addNew': 'New User',
    'users.search': 'Search users...',
    'users.name': 'Name',
    'users.email': 'Email',
    'users.role': 'Role',
    'users.status': 'Status',
    'users.actions': 'Actions',
    'users.active': 'Active',
    'users.inactive': 'Inactive',
    'users.admin': 'Admin',
    'users.user': 'User',
    'users.manager': 'Manager',
    'users.confirmDelete': 'Are you sure you want to delete this user? This action cannot be undone!',
    'users.successMessage': 'User has been successfully deleted!',
    'users.successUpdated': 'User has been successfully updated!',
    
    // Organizations Page
    'organizations.title': 'Organizations',
    'organizations.subtitle': 'Company management',
    'organizations.addNew': 'New Organization',
    'organizations.search': 'Search organizations...',
    'organizations.name': 'Name',
    'organizations.domain': 'Domain',
    'organizations.users': 'Users',
    'organizations.status': 'Status',
    'organizations.actions': 'Actions',
    'organizations.confirmDelete': 'Are you sure you want to delete this organization? This action cannot be undone!',
    'organizations.successMessage': 'Organization has been successfully deleted!',
    'organizations.successUpdated': 'Organization has been successfully updated!',
    'organizations.successAdded': 'Organization has been successfully created!',
    
    // Compliance Page
    'compliance.title': 'Compliance Analysis',
    'compliance.subtitle': 'AI-powered automated analysis',
    'compliance.copyPaste': 'Copy & Paste',
    'compliance.copyPasteDesc': 'Paste your text directly',
    'compliance.uploadFile': 'Upload File',
    'compliance.uploadFileDesc': 'Upload documents',
    'compliance.form': 'Form',
    'compliance.formDesc': 'Fill out a structured form',
    'compliance.startAnalysis': 'Start Analysis',
    'compliance.selectFile': 'Select or drag a file',
    'compliance.dragFile': 'Click or drag a file here',
    'compliance.acceptedFormats': 'Accepted formats: PDF, DOCX, TXT',
    'compliance.send': 'Send',
    'compliance.fillForm': 'Fill in the form',
    'compliance.name': 'Name',
    'compliance.document': 'Document',
    'compliance.description': 'Description',
    'compliance.namePlaceholder': 'Your name',
    'compliance.documentPlaceholder': 'CPF or CNPJ',
    'compliance.descriptionPlaceholder': 'Describe the purpose of this document...',
    
    // Results Page
    'results.title': 'Results',
    'results.subtitle': 'Compliance analysis results',
    'results.search': 'Search results...',
    'results.date': 'Date',
    'results.type': 'Type',
    'results.status': 'Status',
    'results.score': 'Score',
    'results.actions': 'Actions',
    'results.pending': 'Pending',
    'results.completed': 'Completed',
    'results.failed': 'Failed',
    'results.operator': 'Operator',
    'results.content': 'Content',
    'results.noContent': 'No content available',
    'results.download': 'Download',
    'results.copy': 'Copy',
    'results.seeConversation': 'See conversation',
    'results.noResults': 'No results found',
    'results.details': 'Result Details',
    'results.copyContent': 'Copy Content',
    'results.copySuccess': 'Content copied to clipboard!',
    
    // Rules Page
    'rules.title': 'Rules',
    'rules.subtitle': 'Compliance rules & policies',
    'rules.addNew': 'New Rule',
    'rules.search': 'Search rules...',
    'rules.name': 'Name',
    'rules.description': 'Description',
    'rules.category': 'Category',
    'rules.status': 'Status',
    'rules.actions': 'Actions',
    'rules.confirmDelete': 'Are you sure you want to delete this rule? This action cannot be undone!',
    'rules.successMessage': 'Rule has been successfully deleted!',
    'rules.successUpdated': 'Rule has been successfully updated!',
    
    // Forms - User
    'form.user.newUser': 'New User',
    'form.user.editUser': 'Edit User',
    'form.user.userDetails': 'User Details',
    'form.user.name': 'Name',
    'form.user.namePlaceholder': 'Name',
    'form.user.email': 'Email',
    'form.user.emailPlaceholder': 'example@example.com',
    'form.user.password': 'Password',
    'form.user.passwordPlaceholder': '********',
    'form.user.profile': 'Profile',
    'form.user.profilePlaceholder': 'Select a profile',
    'form.user.organization': 'Organization',
    'form.user.organizationPlaceholder': 'Select an organization',
    'form.user.organizationPredefined': 'Organization (Pre-defined)',
    'form.user.phone': 'Phone',
    'form.user.phonePlaceholder': '+1 (xxx) xxx-xxxx or +55 (xx) xxxxx-xxxx',
    'form.user.role': 'Role',
    'form.user.profileAdmin': 'Admin',
    'form.user.profileUser': 'User',
    'form.user.profileManager': 'Manager',
    'form.user.backToUsers': 'Back to users list',
    'form.user.add': 'Add',
    'form.user.update': 'Update',
    'form.user.processing': 'Processing...',
    'form.user.addConfirmation': 'Add Confirmation',
    'form.user.updateConfirmation': 'Update Confirmation',
    'form.user.addConfirmText': 'Are you sure you want to add this user?',
    'form.user.updateConfirmText': 'Are you sure you want to update this user?',
    'form.user.notSpecified': 'Not specified',
    'form.user.userNotFound': 'User not found',
    'form.user.loadingOrganization': 'Loading organization...',
          'form.user.permissions': 'Permissions',
      'form.user.mfaActive': 'MFA Active',
      'form.user.mfaDescription': 'When active, the user will need to enter an additional code when logging in',

    // Forms - Organization
    'form.org.newOrganization': 'New Organization',
    'form.org.editOrganization': 'Edit Organization',
    'form.org.organizationDetails': 'Organization Details',
    'form.org.name': 'Name',
    'form.org.namePlaceholder': 'Name',
    'form.org.email': 'Email',
    'form.org.emailPlaceholder': 'example@example.com',
    'form.org.cnpjTaxId': 'CNPJ / Tax ID',
    'form.org.cnpjPlaceholder': '00.000.000/0001-00',
    'form.org.contactName': 'Contact Name',
    'form.org.contactNamePlaceholder': 'Contact name',
    'form.org.phone': 'Phone',
    'form.org.phonePlaceholder': '+1 (xxx) xxx-xxxx',
    'form.org.backToOrganizations': 'Back to organizations list',
    'form.org.add': 'Add',
    'form.org.update': 'Update',
    'form.org.addConfirmation': 'Add Confirmation',
    'form.org.updateConfirmation': 'Update Confirmation',
    'form.org.addConfirmText': 'Are you sure you want to add this organization?',
    'form.org.updateConfirmText': 'Are you sure you want to update this organization?',
    'form.org.organizationNotFound': 'Organization not found',

    // Forms - Rules
    'form.rule.newRule': 'New Rule',
    'form.rule.editRule': 'Edit Rule',
    'form.rule.addRule': 'Add Rule',
    'form.rule.ruleDetails': 'Rule Details',
    'form.rule.detailsOfRule': 'Details of the Rule',
    'form.rule.ruleName': 'Rule Name',
    'form.rule.ruleNamePlaceholder': 'Enter the rule name',
    'form.rule.instructions': 'Instructions',
    'form.rule.instructionsPlaceholder': 'Enter your instructions here...',
    'form.rule.instructionsDescription': 'Put below the instructions that you want the AI to follow in the treatment of your data. The more details are provided, the better the accuracy of the system.',
    'form.rule.name': 'Name',
    'form.rule.prompt': 'Prompt',
    'form.rule.backToRules': 'Back to rules list',
    'form.rule.save': 'Save',
    'form.rule.saving': 'Saving...',
    'form.rule.successCreated': 'The rule was created successfully!',
    'form.rule.successUpdated': 'The rule was updated successfully!',
    'form.rule.ruleNotFound': 'Rule not found',
    'form.rule.ruleNotFoundDesc': 'It was not possible to find the details of the requested rule.',
    'form.rule.ruleNameRequired': 'Please enter a rule name',
    'form.rule.instructionsRequired': 'Please enter the rule instructions',
    'form.rule.saveError': 'An error occurred while saving the instructions. Please try again.',

    // Validation Messages
    'validation.emailInvalid': 'Please enter a valid email address',
    'validation.passwordMinLength': 'Password must be at least 8 characters long',
    'validation.phoneInvalid': 'Please enter a valid phone number: +1 (xxx) xxx-xxxx or +55 (xx) xxxxx-xxxx',
    'validation.cnpjInvalid': 'Please enter a valid CNPJ in the format 00.000.000/0001-00',

    // Common
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.close': 'Close',
    'common.loading': 'Loading...',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.view': 'View',
    'common.new': 'New',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.export': 'Export',
    'common.import': 'Import',
    'common.refresh': 'Refresh',
    'common.success': 'Success',
  }
};

interface LanguageContextData {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextData>({} as LanguageContextData);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem('@App:language') as Language;
    return savedLanguage || 'pt'; // Português como padrão
  });

  // Função para alterar idioma e salvar no localStorage
  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
    localStorage.setItem('@App:language', newLanguage);
  };

  // Função para traduzir textos
  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  useEffect(() => {
    localStorage.setItem('@App:language', language);
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 